/**
 * AddReceiptCxP.js — Formulario pago a proveedor
 * UI idéntico a AddReceipt: receipt-container (1fr 1fr), receipt-details (izq),
 * cxc-details (der) ahora = "Facturas Pendientes", receipt-footer (bottom).
 * CSS: addReceipt.module.css (mismas clases que AddReceipt).
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiFetch from "../../../../shared/services/apiClient";
import styles from "../../receipts/styles/addReceipt.module.css";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";

const r2  = (n) => Math.round(Number(n) * 100) / 100;
const fmt = (n) => Number(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2 });

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5,
      color:"#f87171", fontSize:"0.77rem", marginTop:4, fontWeight:500 }}>
      <i className="fa-solid fa-circle-exclamation" style={{ fontSize:"0.72rem" }}/>
      {msg}
    </div>
  );
}

const PAGE_SIZE = 10;

export default function AddReceiptCxP() {
  const navigate = useNavigate();

  // ── Form state ────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    fecha_pago:      new Date().toISOString().split("T")[0],
    monto:           "",
    metodo_pago:     "TRANSFERENCIA",
    documento:       "",
    cuenta_bancaria: null,
    cuenta_proveedor: null,
    cuenta_proveedor_nombre: "",
  });

  // ── Data ──────────────────────────────────────────────────────────────────
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [cuentasProveedor, setCuentasProveedor] = useState([]);
  const [facturasCxP,      setFacturasCxP]      = useState([]);
  const [selectedFacturas, setSelectedFacturas] = useState(new Set());

  // ── UI ────────────────────────────────────────────────────────────────────
  const [errors,     setErrors]     = useState({});
  const [saving,     setSaving]     = useState(false);
  const [apiErr,     setApiErr]     = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loadingFac, setLoadingFac] = useState(false);
  const [page,       setPage]       = useState(1);

  // ── Load banks and proveedores ─────────────────────────────────────────────
  useEffect(() => {
    apiFetch("/api/finance/cuentaBancaria/")
      .then(r => r.json())
      .then(d => {
        const list = d.results ?? d ?? [];
        setCuentasBancarias(list);
        if (list.length > 0) setForm(p => ({ ...p, cuenta_bancaria: list[0].id }));
      });
    apiFetch("/api/finance/catalogo/detalle/?tipo=PASIVO&padre_nombre=Cuentas+por+Pagar+Locales&page_size=500")
      .then(r => r.json())
      .then(d => setCuentasProveedor(Array.isArray(d) ? d : (d.results ?? [])));
  }, []);

  // ── Load facturas when proveedor changes ───────────────────────────────────
  useEffect(() => {
    if (!form.cuenta_proveedor) { setFacturasCxP([]); setSelectedFacturas(new Set()); return; }
    setLoadingFac(true);
    apiFetch(`/api/finance/cuentaXPagar/?page_size=9999`)
      .then(r => r.json())
      .then(d => {
        const all = d.results ?? [];
        setFacturasCxP(all.filter(
          cxp => cxp.factura_cxp?.cuenta_proveedor?.id === form.cuenta_proveedor && Number(cxp.saldo) > 0
        ));
        setSelectedFacturas(new Set());
        setPage(1);
      })
      .finally(() => setLoadingFac(false));
  }, [form.cuenta_proveedor]);

  // Auto-set monto when facturas are selected
  useEffect(() => {
    if (selectedFacturas.size === 0) return;
    const total = facturasCxP
      .filter(f => selectedFacturas.has(f.id))
      .reduce((s, f) => s + Number(f.saldo), 0);
    setForm(p => ({ ...p, monto: String(r2(total)) }));
  }, [selectedFacturas, facturasCxP]);

  function set(field, val) { setForm(p => ({ ...p, [field]: val })); }

  function handleProveedor(e) {
    const id  = Number(e.target.value) || null;
    const obj = cuentasProveedor.find(c => c.id === id);
    setForm(p => ({ ...p, cuenta_proveedor: id, cuenta_proveedor_nombre: obj?.nombre ?? "" }));
  }

  function toggleFactura(id) {
    setSelectedFacturas(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function toggleAll() {
    setSelectedFacturas(
      selectedFacturas.size === facturasCxP.length
        ? new Set()
        : new Set(facturasCxP.map(f => f.id))
    );
  }

  function validate() {
    const e = {};
    if (!form.cuenta_proveedor)                  e.cuenta_proveedor = "Seleccioná un proveedor";
    if (!form.fecha_pago)                        e.fecha_pago       = "La fecha de pago es requerida";
    if (!form.monto || Number(form.monto) <= 0)  e.monto            = "El monto debe ser mayor a 0";
    if (!form.cuenta_bancaria)                   e.cuenta_bancaria  = "Seleccioná una cuenta bancaria";
    if (!form.documento.trim())                  e.documento        = "La referencia es obligatoria";
    if (selectedFacturas.size === 0)              e.facturas         = "Seleccioná al menos una factura CxP";
    // Validar saldo bancario
    if (form.cuenta_bancaria && form.monto) {
      const banco = cuentasBancarias.find(b => b.id === form.cuenta_bancaria);
      if (banco && Number(form.monto) > Number(banco.saldo || 0)) {
        e.cuenta_bancaria = `Saldo insuficiente — disponible: ₡${fmt(banco.saldo || 0)}`;
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    setApiErr(""); setSuccessMsg("");
    if (!validate()) return;
    setSaving(true);
    try {
      const facturasToProcess = facturasCxP.filter(f => selectedFacturas.has(f.id));
      if (facturasToProcess.length === 0) {
        setApiErr('Seleccioná al menos una factura CxP.');
        setSaving(false);
        return;
      }
      let lastNumero = "";
      for (const cxp of facturasToProcess) {
        const res = await apiFetch("/api/finance/reciboCxP/", {
          method: "POST",
          body: JSON.stringify({
            fecha_pago:      form.fecha_pago,
            monto:           Number(form.monto),  // user-entered amount (can be partial)
            metodo_pago:     form.metodo_pago,
            documento:       form.documento.trim(),
            cuenta_bancaria: form.cuenta_bancaria,
            factura_cxp:     cxp?.factura_cxp?.id ?? null,
          }),
        });
        if (!res.ok) {
          const err = await res.json();
          const k = Object.keys(err)[0];
          throw new Error(k ? `${k}: ${Array.isArray(err[k]) ? err[k][0] : err[k]}` : "Error al guardar");
        }
        const created = await res.json();
        lastNumero = created.numero_recibo;
      }
      setSuccessMsg(`Pago ${lastNumero} registrado. Asiento de diario generado.`);
      // Refresh facturas
      if (form.cuenta_proveedor) {
        apiFetch(`/api/finance/cuentaXPagar/?page_size=9999`)
          .then(r => r.json())
          .then(d => {
            const all = d.results ?? [];
            setFacturasCxP(all.filter(
              cxp => cxp.factura_cxp?.cuenta_proveedor?.id === form.cuenta_proveedor && Number(cxp.saldo) > 0
            ));
          });
      }
      setSelectedFacturas(new Set());
      setForm(p => ({ ...p, monto: "", documento: "" }));
      setErrors({});
      setPage(1);
    } catch (ex) { setApiErr(ex.message); }
    finally { setSaving(false); }
  }

  const totalSeleccionado = facturasCxP
    .filter(f => selectedFacturas.has(f.id))
    .reduce((s, f) => s + Number(f.saldo), 0);

  const totalPendienteProveedor = facturasCxP.reduce((s, f) => s + Number(f.saldo), 0);

  const totalPages = Math.ceil(facturasCxP.length / PAGE_SIZE);
  const visibles   = facturasCxP.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const errorBorder = (field) => errors[field] ? { borderColor:"#ef4444" } : undefined;
  const montoNum = Number(form.monto) || 0;
  const bancoDato = cuentasBancarias.find(b => b.id === form.cuenta_bancaria);
  const saldoInsuficiente = bancoDato && montoNum > 0 && montoNum > Number(bancoDato.saldo || 0);

  return (
    <>
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>Inicio /</span>
        </Link>
        <Link to="/finanzas/cxp">
          <span className={styles.inactive}> Cuentas por Pagar /</span>
        </Link>
        <Link to="/finanzas/cxp/recibos">
          <span className={styles.inactive}> Recibos /</span>
        </Link>
        <span className={styles.active}> Agregar</span>
      </span>

      <h2 className="mt-2 me-2 p-0 title">Registrar Pago a Proveedor</h2>

      {/* Éxito */}
      {successMsg && (
        <div style={{ background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.3)",
          borderLeft:"4px solid #4ade80", borderRadius:8, padding:"12px 18px", marginBottom:16,
          display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
          <span style={{ color:"#4ade80", fontWeight:500, fontSize:"0.9rem" }}>
            <i className="fa-solid fa-circle-check" style={{ marginRight:8 }}/>{successMsg}
          </span>
          <button onClick={() => setSuccessMsg("")}
            style={{ background:"none", border:"none", color:"#4ade80", cursor:"pointer" }}>✕</button>
        </div>
      )}

      {/* Error API */}
      {apiErr && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px", marginBottom:16,
          color:"#f87171", fontSize:"0.88rem", display:"flex", alignItems:"center", gap:8 }}>
          <i className="fa-solid fa-circle-exclamation"/>{apiErr}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className={styles["receipt-container"]}>

          {/* ── Izquierda: Detalles del Pago — idéntico a ReceiptDetails ── */}
          <div className={styles["receipt-details"]}>
            <div className={styles["receipt-details-header"]}>
              <h1>
                <i className={`fa-solid fa-money-bill-transfer ${styles["icon-receipt-details"]}`}/>
                Detalles del Pago
              </h1>
            </div>
            <div className={styles["receipt-details-body"]} style={{ marginTop:14 }}>

              {/* Proveedor */}
              <div>
                <label className="form-label">Proveedor <span style={{ color:"#f87171" }}>*</span></label>
                <select className="form-select" value={form.cuenta_proveedor ?? ""}
                  onChange={handleProveedor} style={errorBorder("cuenta_proveedor")}>
                  <option value="">— Seleccioná proveedor —</option>
                  {cuentasProveedor.map(c => (
                    <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                  ))}
                </select>
                <FieldError msg={errors.cuenta_proveedor}/>
              </div>

              {/* Fecha */}
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label className="form-label">Fecha de Pago <span style={{ color:"#f87171" }}>*</span></label>
                    <input className="form-control" type="date" value={form.fecha_pago}
                      onChange={e => set("fecha_pago", e.target.value)}
                      style={errorBorder("fecha_pago")}/>
                    <FieldError msg={errors.fecha_pago}/>
                  </div>
                </div>
                <div className="col">
                  <div className="form-group">
                    <label className="form-label">Medio de Pago</label>
                    <select className="form-select" value={form.metodo_pago}
                      onChange={e => set("metodo_pago", e.target.value)}>
                      <option value="TRANSFERENCIA">Transferencia</option>
                      <option value="EFECTIVO">Efectivo</option>
                      <option value="CHEQUE">Cheque</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Monto */}
              <div className="row">
                <div className="form-group">
                  <label className="form-label">Monto (₡) <span style={{ color:"#f87171" }}>*</span></label>
                  <input className="form-control" type="number" min="0.01" step="0.01"
                    placeholder="0.00" value={form.monto}
                    onChange={e => set("monto", e.target.value)}
                    style={errorBorder("monto")}/>
                  <FieldError msg={errors.monto}/>
                  {selectedFacturas.size > 0 && (
                    <div style={{ fontSize:"0.73rem", color:"#f97316", marginTop:3 }}>
                      Auto-calculado del saldo seleccionado: ₡{fmt(totalSeleccionado)}
                    </div>
                  )}
                </div>
              </div>

              {/* Cuenta bancaria */}
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label className="form-label">Cuenta Bancaria <span style={{ color:"#f87171" }}>*</span></label>
                    {cuentasBancarias.length === 0 ? (
                      <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.3)",
                        borderRadius:6, padding:"8px 12px", color:"#f87171", fontSize:"0.82rem",
                        display:"flex", alignItems:"center", gap:7 }}>
                        <i className="fa-solid fa-triangle-exclamation"/>
                        No hay cuentas bancarias. Configuralas en Settings.
                      </div>
                    ) : (
                      <>
                        <select className="form-select"
                          value={form.cuenta_bancaria ?? ""}
                          onChange={e => set("cuenta_bancaria", Number(e.target.value) || null)}
                          style={errorBorder("cuenta_bancaria")}>
                          {cuentasBancarias.map(cb => (
                            <option key={cb.id} value={cb.id}>
                              {cb.banco} — {cb.numero_cuenta} — Saldo: ₡{fmt(cb.saldo || 0)}
                            </option>
                          ))}
                        </select>
                        <FieldError msg={errors.cuenta_bancaria}/>
                        {saldoInsuficiente && (
                          <div style={{ marginTop:4, padding:"5px 10px",
                            background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.3)",
                            borderRadius:6, color:"#f87171", fontSize:"0.8rem",
                            display:"flex", alignItems:"center", gap:6 }}>
                            <i className="fa-solid fa-triangle-exclamation"/>
                            Saldo insuficiente — disponible: ₡{fmt(bancoDato?.saldo || 0)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Referencia */}
              <div className="row">
                <div className="form-group">
                  <label className="form-label">Referencia Bancaria <span style={{ color:"#f87171" }}>*</span></label>
                  <input className="form-control" type="text"
                    placeholder="N° de transferencia, cheque, etc."
                    value={form.documento}
                    onChange={e => set("documento", e.target.value)}
                    style={errorBorder("documento")}/>
                  <FieldError msg={errors.documento}/>
                </div>
              </div>

              {/* Info asiento */}
              <div style={{ padding:"10px 14px", background:"rgba(96,165,250,0.06)",
                border:"1px solid rgba(96,165,250,0.2)", borderRadius:6,
                fontSize:"0.8rem", color:"#93c5fd" }}>
                <i className="fa-solid fa-book-open" style={{ marginRight:6 }}/>
                Asiento: <strong>DEBE</strong> {form.cuenta_proveedor_nombre || "Proveedor (200)"}
                {" · "}<strong>HABER</strong> Banco
              </div>

            </div>
          </div>

          {/* ── Derecha: Facturas Pendientes — idéntico a CxcDetails ── */}
          <div className={styles["cxc-details"]}>
            <div className={styles["cxc-details-header"]}>
              <h1>
                <i className={`fa-solid fa-file-invoice ${styles["icon-cxc-details"]}`}/>
                Facturas Pendientes
              </h1>
            </div>

            <div className={styles["receipt-details-body"]}>
              {!form.cuenta_proveedor ? (
                <div style={{ padding:"40px 16px", textAlign:"center", color:"#6b7280" }}>
                  <i className="fa-solid fa-building" style={{ fontSize:"2rem", display:"block",
                    marginBottom:12, color:"#374151" }}/>
                  Seleccioná un proveedor para ver sus facturas pendientes
                </div>
              ) : loadingFac ? (
                <div style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>
                  <i className="fa-solid fa-circle-notch fa-spin"
                     style={{ color:"#f97316", fontSize:"1.3rem" }}/>
                </div>
              ) : facturasCxP.length === 0 ? (
                <div style={{ padding:32, textAlign:"center", color:"#6b7280", fontSize:"0.87rem" }}>
                  <i className="fa-solid fa-check-circle" style={{ color:"#4ade80",
                    fontSize:"1.4rem", display:"block", marginBottom:8 }}/>
                  No hay facturas pendientes para este proveedor
                </div>
              ) : (
                <>
                  {/* Tabla de facturas — igual estructura que InvoiceCxc */}
                  <div style={{ width:"100%", overflowX:"auto",
                    border:"1px solid #364250", borderRadius:8 }}>
                    <table style={{ width:"100%", borderCollapse:"collapse",
                      color:"#e5ecf5", minWidth:0 }}>
                      <thead>
                        <tr style={{ background:"#1f2937",
                          borderBottom:"1px solid #364250" }}>
                          <th style={{ width:36, padding:"10px 12px",
                            textAlign:"center", color:"#8d98a7",
                            fontSize:"0.8rem", fontWeight:500 }}>
                            <input type="checkbox"
                              checked={selectedFacturas.size === facturasCxP.length}
                              onChange={toggleAll}
                              style={{ accentColor:"#f97316" }}/>
                          </th>
                          <th style={{ padding:"10px 12px", textAlign:"left",
                            color:"#8d98a7", fontSize:"0.8rem", fontWeight:500,
                            width:"30%" }}>Factura</th>
                          <th style={{ padding:"10px 12px", textAlign:"left",
                            color:"#8d98a7", fontSize:"0.8rem", fontWeight:500 }}>Concepto</th>
                          <th style={{ padding:"10px 12px", textAlign:"right",
                            color:"#8d98a7", fontSize:"0.8rem", fontWeight:500,
                            width:"28%" }}>Saldo</th>
                        </tr>
                      </thead>
                        <tbody>
                          {visibles.map(cxp => {
                            const fac = cxp.factura_cxp;
                            const sel = selectedFacturas.has(cxp.id);
                            return (
                              <tr key={cxp.id} style={{
                                background: sel ? "rgba(249,115,22,0.08)" : "transparent",
                                cursor: "pointer",
                                borderBottom: "1px solid #202a33",
                              }} onClick={() => toggleFactura(cxp.id)}>
                                <td style={{ width:36, padding:"10px 12px",
                                  textAlign:"center" }}>
                                  <input type="checkbox" checked={sel}
                                    onChange={() => toggleFactura(cxp.id)}
                                    onClick={e => e.stopPropagation()}
                                    style={{ accentColor:"#f97316" }}/>
                                </td>
                                <td style={{ padding:"10px 12px", fontFamily:"monospace",
                                  color:"#f97316", fontWeight:600, fontSize:"0.83rem" }}>
                                  {fac?.numero_factura ?? "—"}
                                </td>
                                <td style={{ padding:"10px 12px", color:"#8d98a7",
                                  fontSize:"0.8rem", overflow:"hidden",
                                  textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                  {cxp.concepto || fac?.concepto || "—"}
                                </td>
                                <td style={{ padding:"10px 12px", textAlign:"right",
                                  fontFamily:"monospace", color:"#facc15",
                                  fontWeight:600 }}>
                                  ₡{fmt(cxp.saldo)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                    </table>
                    {/* Footer paginación */}
                    <div style={{ width:"100%", textAlign:"center",
                      padding:"8px 0", fontSize:13, color:"#8d98a7",
                      background:"#1f2937", borderTop:"1px solid #364250",
                      boxSizing:"border-box" }}>
                      {facturasCxP.length} factura(s) pendiente(s) · Página {page} de {Math.max(1, totalPages)}
                      {totalPages > 1 && (
                        <span style={{ marginLeft:16 }}>
                          <button type="button" disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                            style={{ background:"none", border:"1px solid #364250",
                              borderRadius:4, color:"#8d98a7", cursor:"pointer",
                              padding:"2px 8px", marginRight:6 }}>←</button>
                          <button type="button" disabled={page >= totalPages}
                            onClick={() => setPage(p => p + 1)}
                            style={{ background:"none", border:"1px solid #364250",
                              borderRadius:4, color:"#8d98a7", cursor:"pointer",
                              padding:"2px 8px" }}>→</button>
                        </span>
                      )}
                  </div>
                  </div>

                  {/* Resumen — igual que CxcSummary */}
                  <div className={styles["cxc-details-summary"]}>
                    <div className={styles["cxc-summary-relevant"]}>
                      <h2>Resumen Proveedor</h2>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <span>Total pendiente proveedor</span>
                        <span style={{ color:"#f87171", fontWeight:700 }}>₡{fmt(totalPendienteProveedor)}</span>
                      </div>
                      {selectedFacturas.size > 0 && (
                        <div style={{ display:"flex", justifyContent:"space-between" }}>
                          <span>Seleccionado ({selectedFacturas.size})</span>
                          <span style={{ color:"#f97316", fontWeight:700 }}>₡{fmt(totalSeleccionado)}</span>
                        </div>
                      )}
                    </div>
                    <div className={styles["cxc-summary-important"]}>
                      <h2>Total a pagar</h2>
                      <span>₡{fmt(montoNum)}</span>
                    </div>
                  </div>
                  </>
              )}
            </div>
          </div>

          {/* ── Footer — full width ── */}
          <div className={styles["receipt-footer"]}>
            <div className={styles.actions}>
              <Link to="/finanzas/cxp/recibos"
                className={`${styles.btn} ${styles["btn-clean-form"]}`}
                style={{ textDecoration:"none" }}>
                Cancelar
              </Link>
              <button type="submit" disabled={saving}
                className={`${styles.btn} ${styles["btn-save"]}`}
                style={{ opacity: saving ? 0.7 : 1 }}>
                {saving
                  ? <><i className="fa-solid fa-circle-notch fa-spin"/> Guardando…</>
                  : <><i className="fa-solid fa-lock"/> Registrar Pago</>}
              </button>
            </div>
          </div>

        </div>
      </form>
    </>
  );
}
